import { GeneralService } from "./GeneralService";
import { ApiKeyOrGetter } from "./GeneralService";

interface IngestResponse {
  success: boolean;
  job_name?: string;
  request_count?: number;
  response_count?: number;
  message: string;
  error?: string;
  s3_key?: string;
  s3_path?: string;
}

export class DataIngestService {
  private static async performUpload(
    file: File,
    companyId: number,
    applicationRefName: string,
    actionType: string,
    batchSize: number,
    concurrentWorkers: number,
    useLocalProcessing: boolean,
    useStreaming: boolean,
    tokenString: string,
    onProgress?: (progress: number) => void
  ): Promise<IngestResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress tracking
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            );
            onProgress(percentComplete);
          }
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (onProgress) onProgress(100);

            if (typeof response !== "object" || response === null) {
              reject({
                success: false,
                message: "Invalid response format",
                error: "Server returned an invalid response format",
              } as IngestResponse);
              return;
            }

            if (typeof response.success !== "boolean") {
              reject({
                success: false,
                message: "Invalid response structure",
                error: "Server response missing required fields",
              } as IngestResponse);
              return;
            }

            if (
              response.success === false &&
              !response.error &&
              !response.message
            ) {
              response.error = "Unknown error occurred";
            }

            resolve(response as IngestResponse);
          } catch (e) {
            if (onProgress) onProgress(0);
            reject({
              success: false,
              message: "Invalid response data",
              error: "Unable to parse server response",
            } as IngestResponse);
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            if (onProgress) onProgress(0);

            const validatedErrorResponse: IngestResponse = {
              success: false,
              message: "Upload failed",
              error: errorResponse.error || `Failed with status ${xhr.status}`,
            };

            // Include status code in error message for 401 detection
            if (xhr.status === 401) {
              validatedErrorResponse.error = `401 Unauthorized: ${validatedErrorResponse.error}`;
            }

            reject(validatedErrorResponse);
          } catch (e) {
            if (onProgress) onProgress(0);
            const errorMessage =
              xhr.status === 401
                ? `401 Unauthorized: Server error with status ${xhr.status}`
                : `Server error with status ${xhr.status}`;
            reject({
              success: false,
              message: "Upload failed",
              error: errorMessage,
            } as IngestResponse);
          }
        }
      };
      xhr.onerror = () => {
        if (onProgress) onProgress(0);
        reject({
          success: false,
          message: "Network error",
          error: "Network error occurred during upload and ingest",
        } as IngestResponse);
      };

      const endpoint = new URL(
        "/data_ingestion/upload_and_ingest",
        GeneralService.PROD_ENDPOINT
      );
      endpoint.searchParams.append("fileName", file.name);
      endpoint.searchParams.append("companyId", String(companyId));
      endpoint.searchParams.append("applicationRefName", applicationRefName);
      endpoint.searchParams.append("actionType", actionType);
      endpoint.searchParams.append("batchSize", batchSize.toString());
      endpoint.searchParams.append(
        "concurrentBatches",
        concurrentWorkers.toString()
      );
      endpoint.searchParams.append(
        "useLocalProcessing",
        useLocalProcessing.toString()
      );
      endpoint.searchParams.append("useStreaming", useStreaming.toString());

      // Open connection and send the file
      xhr.open("POST", endpoint.toString(), true);

      // Check if tokenString is a JWT token (has 2 dots) or a regular API key
      if (tokenString && tokenString.split(".").length === 3) {
        // It's a JWT token, use Authorization header
        xhr.setRequestHeader("Authorization", `Bearer ${tokenString}`);
      } else {
        // It's a regular API key, use x-api-key header
        xhr.setRequestHeader("x-api-key", tokenString);
      }

      xhr.setRequestHeader(
        "Content-Type",
        file.type || "application/octet-stream"
      );
      xhr.send(file);
    });
  }

  public static async uploadAndIngest(
    file: File,
    companyId: number,
    applicationRefName: string,
    actionType: string,
    batchSize: number = 1000,
    concurrentWorkers: number = 5,
    useLocalProcessing: boolean = true,
    useStreaming: boolean = true,
    apiKey: ApiKeyOrGetter,
    onProgress?: (progress: number) => void
  ): Promise<IngestResponse> {
    // Get initial token
    let tokenString: string;
    if (typeof apiKey === "string") {
      tokenString = apiKey;
    } else if (typeof apiKey === "object") {
      tokenString = await apiKey.getToken();
    } else {
      throw new Error("Invalid API key type");
    }

    try {
      // Attempt upload with current token
      return await this.performUpload(
        file,
        companyId,
        applicationRefName,
        actionType,
        batchSize,
        concurrentWorkers,
        useLocalProcessing,
        useStreaming,
        tokenString,
        onProgress
      );
    } catch (error: any) {
      // If we get a 401 and have a refresh function, retry once with fresh token
      if (
        error &&
        error.error &&
        typeof error.error === "string" &&
        error.error.includes("401") &&
        typeof apiKey === "object"
      ) {
        console.log("Upload received 401, refreshing token and retrying...");
        try {
          const freshToken = await apiKey.refreshToken();
          return await this.performUpload(
            file,
            companyId,
            applicationRefName,
            actionType,
            batchSize,
            concurrentWorkers,
            useLocalProcessing,
            useStreaming,
            freshToken,
            onProgress
          );
        } catch (retryError) {
          console.error("Token refresh failed:", retryError);
          throw retryError;
        }
      }
      // For any other error, just rethrow
      throw error;
    }
  }
}
